import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCheckLight,
  PiScalesLight,
  PiGavelLight,
  PiDatabaseLight,
  PiListChecksLight,
  PiChatTextLight,
  PiTextAlignLeftLight,
  PiNoteLight,
  PiSparkleLight,
  PiFileTextLight,
  PiLightningLight,
  PiMagnifyingGlassLight,
  PiSlidersHorizontalLight,
} from 'react-icons/pi';
import Toggle from '../shared/TailwindComponents/Toggle';
import { FIELD_LABEL_CLASS, FIELD_GAP } from '../shared/TailwindComponents/FieldShell';
import Button from '../shared/TailwindComponents/Button';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import IndexSourceSelector from './IndexSourceSelector';
import { hasCasesSupport } from './matterConstants';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, analyzeWizardTourSteps } from '../shared/FeatureTour';

// ─── Types ───────────────────────────────────────────────────────────

type WizardStep = 'research' | 'sources' | 'review';

interface AnalysisOptions {
  [key: string]: boolean;
  fetchLaws: boolean;
  fetchPrecedents: boolean;
  fetchDataSource: boolean;
  createTasks: boolean;
  createQuestionnaire: boolean;
  generateDescription: boolean;
  generateNotes: boolean;
  generateDraftSuggestions: boolean;
  generateInsightSuggestions: boolean;
}

export interface AnalyzeWizardResult {
  analysisOptions: AnalysisOptions;
  selectedDataSourceUuids: string[];
  selectedIndexes: string[];
}

interface AnalyzeWizardModalProps {
  jurisdiction: string;
  hasBeenAnalyzed: boolean;
  availableDataSources: { value: string; label: string }[];
  onLaunch: (config: AnalyzeWizardResult) => void;
  onClose: () => void;
}

// ─── Step definitions ────────────────────────────────────────────────

const STEPS: { key: WizardStep; label: string; icon: React.ReactNode }[] = [
  { key: 'research', label: 'Research', icon: <PiMagnifyingGlassLight className="w-4 h-4" /> },
  { key: 'sources', label: 'Sources', icon: <PiDatabaseLight className="w-4 h-4" /> },
  { key: 'review', label: 'Review & Launch', icon: <PiSlidersHorizontalLight className="w-4 h-4" /> },
];

const DEFAULT_OPTIONS: AnalysisOptions = {
  fetchLaws: true,
  fetchPrecedents: true,
  fetchDataSource: true,
  createTasks: true,
  createQuestionnaire: true,
  generateDescription: true,
  generateNotes: true,
  generateDraftSuggestions: true,
  generateInsightSuggestions: true,
};

// ─── Toggle card helper ──────────────────────────────────────────────

interface ToggleOpt {
  key: keyof AnalysisOptions;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const ToggleCard: React.FC<{
  opt: ToggleOpt;
  enabled: boolean;
  onToggle: () => void;
}> = ({ opt, enabled, onToggle }) => (
  <div
    className={`flex items-start justify-between gap-3 rounded-lg border p-3 transition-all duration-200 ${
      enabled
        ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/20'
        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
    }`}
  >
    <div className="flex items-start gap-3 min-w-0">
      <div
        className={`flex-shrink-0 mt-0.5 transition-colors duration-200 ${
          enabled
            ? 'text-primary-600 dark:text-primary-400'
            : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        {opt.icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.description}</p>
      </div>
    </div>
    <div className="flex-shrink-0 pt-0.5">
      <Toggle enabled={enabled} onToggle={onToggle} />
    </div>
  </div>
);

// ─── Component ───────────────────────────────────────────────────────

const AnalyzeWizardModal: React.FC<AnalyzeWizardModalProps> = ({
  jurisdiction,
  hasBeenAnalyzed,
  availableDataSources,
  onLaunch,
  onClose,
}) => {
  const { t } = useTranslation();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('research');
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
  const isLastStep = currentIdx === STEPS.length - 1;

  // Analysis configuration state
  const [options, setOptions] = useState<AnalysisOptions>(DEFAULT_OPTIONS);
  const [selectedDataSourceUuids, setSelectedDataSourceUuids] = useState<string[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<string[]>([]);

  const showCases = hasCasesSupport(jurisdiction);
  const hasDataSources = availableDataSources.length > 0;

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'analyze-wizard',
    steps: analyzeWizardTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  // ─── Navigation ──────────────────────────────────────────────────

  const goNext = useCallback(() => {
    if (currentIdx < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIdx + 1].key);
    }
  }, [currentIdx]);

  const goPrev = useCallback(() => {
    if (currentIdx > 0) {
      setCurrentStep(STEPS[currentIdx - 1].key);
    }
  }, [currentIdx]);

  const handleLaunch = useCallback(() => {
    onLaunch({
      analysisOptions: options,
      selectedDataSourceUuids,
      selectedIndexes,
    });
  }, [options, selectedDataSourceUuids, selectedIndexes, onLaunch]);

  const toggleOption = useCallback((key: keyof AnalysisOptions) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ─── Step indicator ──────────────────────────────────────────────

  const renderStepIndicator = () => (
    <nav className="px-6 pt-4 pb-2" data-tour-id="analyze-wizard-steps">
      <ol className="flex items-center gap-2">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <li key={step.key} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                    isCompleted
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : isCurrent
                      ? 'border-primary-500 bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500'
                  }`}
                >
                  {isCompleted ? <PiCheckLight className="w-4 h-4" /> : step.icon}
                </div>
                <span
                  className={`text-xs font-medium truncate ${
                    isCurrent
                      ? 'text-primary-600 dark:text-primary-400'
                      : isCompleted
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {t(step.label)}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-px transition-colors duration-200 ${
                    idx < currentIdx ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  // ─── Step 1: Research Configuration ──────────────────────────────

  const renderResearchStep = () => {
    const researchToggles: ToggleOpt[] = [
      {
        key: 'fetchLaws',
        label: t('Applicable laws'),
        description: t('Search for statutes, regulations, and legal provisions relevant to your matter.'),
        icon: <PiScalesLight className="w-5 h-5" />,
      },
      ...(showCases
        ? [{
            key: 'fetchPrecedents' as const,
            label: t('Precedents / Case law'),
            description: t('Find court rulings and judicial decisions that may apply to your matter.'),
            icon: <PiGavelLight className="w-5 h-5" />,
          }]
        : []),
      ...(hasDataSources
        ? [{
            key: 'fetchDataSource' as const,
            label: t('Connected data sources'),
            description: t('Search your own uploaded document collections for relevant information.'),
            icon: <PiDatabaseLight className="w-5 h-5" />,
          }]
        : []),
    ];

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            {t('Research configuration')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            {t('Choose which legal databases and sources to search during analysis.')}
          </p>
        </div>
        <div className="space-y-2">
          {researchToggles.map((opt) => (
            <ToggleCard key={opt.key} opt={opt} enabled={options[opt.key]} onToggle={() => toggleOption(opt.key)} />
          ))}
        </div>
      </div>
    );
  };

  // ─── Step 2: Source Selection ────────────────────────────────────

  const renderSourcesStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          {t('Select sources')}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {t('Choose which legal sources and data sources to include in the analysis.')}
        </p>
      </div>

      {/* Legal sources */}
      <IndexSourceSelector
        value={selectedIndexes}
        onChange={setSelectedIndexes}
        jurisdiction={jurisdiction}
      />

      {/* Data Source Selection */}
      {hasDataSources && options.fetchDataSource && (
        <div className="mt-4">
          <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP} flex items-center gap-1`}>
            <PiDatabaseLight className="h-3 w-3" />
            {t('Data sources')}
          </p>
          <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar-main rounded-lg border border-gray-200 dark:border-gray-700 p-2">
            {availableDataSources.map((ds) => (
              <CheckBox
                key={ds.value}
                label={ds.label}
                translate={false}
                checked={selectedDataSourceUuids.includes(ds.value)}
                onChange={() => {
                  setSelectedDataSourceUuids((prev) =>
                    prev.includes(ds.value)
                      ? prev.filter((id) => id !== ds.value)
                      : [...prev, ds.value]
                  );
                }}
                className="rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              />
            ))}
          </div>
          {selectedDataSourceUuids.length > 0 && (
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
              {selectedDataSourceUuids.length} {t('selected')}
            </p>
          )}
        </div>
      )}
    </div>
  );

  // ─── Step 3: Review & Launch ─────────────────────────────────────

  const renderReviewStep = () => {
    const generationToggles: ToggleOpt[] = [
      {
        key: 'createTasks',
        label: t('Suggested tasks'),
        description: t('Generate a prioritized action plan with recommended next steps.'),
        icon: <PiListChecksLight className="w-5 h-5" />,
      },
      {
        key: 'createQuestionnaire',
        label: t('Client questionnaire'),
        description: t('Create a tailored set of questions to gather key information from your client.'),
        icon: <PiChatTextLight className="w-5 h-5" />,
      },
    ];

    const contentToggles: ToggleOpt[] = [
      {
        key: 'generateDescription',
        label: t('Matter description'),
        description: t('Produce a concise summary describing the scope and context of the matter.'),
        icon: <PiTextAlignLeftLight className="w-5 h-5" />,
      },
      {
        key: 'generateNotes',
        label: t('Case notes'),
        description: t('Extract key facts, dates, and observations into structured case notes.'),
        icon: <PiNoteLight className="w-5 h-5" />,
      },
      {
        key: 'generateDraftSuggestions',
        label: t('Draft suggestions'),
        description: t('Suggest document drafts such as letters, contracts, or legal briefs.'),
        icon: <PiFileTextLight className="w-5 h-5" />,
      },
      {
        key: 'generateInsightSuggestions',
        label: t('Insight suggestions'),
        description: t('Surface strategic insights and potential risks based on the analysis.'),
        icon: <PiSparkleLight className="w-5 h-5" />,
      },
    ];

    const enabledCount = Object.values(options).filter(Boolean).length;

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            {t('Review & launch')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            {t('Choose which AI features to run and review your configuration before launching.')}
          </p>
        </div>

        {/* AI Generation */}
        <div>
          <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>
            {t('AI generation')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {generationToggles.map((opt) => (
              <ToggleCard key={opt.key} opt={opt} enabled={options[opt.key]} onToggle={() => toggleOption(opt.key)} />
            ))}
          </div>
        </div>

        {/* Content Generation */}
        <div>
          <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>
            {t('Content generation')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {contentToggles.map((opt) => (
              <ToggleCard key={opt.key} opt={opt} enabled={options[opt.key]} onToggle={() => toggleOption(opt.key)} />
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-100">{enabledCount}</span>{' '}
            {t('features enabled')}
            {selectedIndexes.length > 0 && (
              <>
                {' · '}
                <span className="font-medium text-gray-900 dark:text-gray-100">{selectedIndexes.length}</span>{' '}
                {t('sources selected')}
              </>
            )}
            {selectedDataSourceUuids.length > 0 && (
              <>
                {' · '}
                <span className="font-medium text-gray-900 dark:text-gray-100">{selectedDataSourceUuids.length}</span>{' '}
                {t('data sources')}
              </>
            )}
          </p>
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col">
      {renderStepIndicator()}

      {/* Step content */}
      <div className="px-6 py-4 max-h-[60vh] overflow-y-auto custom-scrollbar-main" data-tour-id="analyze-wizard-content">
        {currentStep === 'research' && renderResearchStep()}
        {currentStep === 'sources' && renderSourcesStep()}
        {currentStep === 'review' && renderReviewStep()}
      </div>

      {/* Footer navigation */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700" data-tour-id="analyze-wizard-footer">
        <Button
          variant="neutral"
          size="small"
          onClick={currentIdx === 0 ? onClose : goPrev}
        >
          {currentIdx === 0 ? t('Cancel') : t('Back')}
        </Button>

        {isLastStep ? (
          <Button
            variant="primary"
            size="small"
            onClick={handleLaunch}
            icon={<PiLightningLight className="h-4 w-4" />}
          >
            {hasBeenAnalyzed ? t('Re-analyze') : t('Run analysis')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="small"
            onClick={goNext}
          >
            {t('Next')}
          </Button>
        )}
      </div>

      <FeatureTour
        steps={analyzeWizardTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default AnalyzeWizardModal;
